import { IUserNotification, TableauEntityType } from '@tableau/user-notifications-client-core';
import _ from 'underscore';

import { IUserIdentityConflictInfo } from '~src/authentication/models/IUserIdentityConflictInfo';
import { IGetUserIdentityConflictsResult } from '~src/authentication/requests/GetUserIdentityConflictsRequest';
import { IUserIdentityConflict } from '~src/authentication/requests/types/IUserIdentityConflict';

import { IDataSourceInfo } from '../../datasource/models/IDataSourceInfo';
import { DatasourceMapper } from '../../datasource/models/mappers/DatasourceMapper';
import { IGetDatasourcesResult } from '../../datasource/ServerApi/requests/GetDatasourcesRequest';
import { IMetricInfo } from '../../metrics/models/IMetricInfo';
import { IGetUserNotificationsResult } from '../../userNotifications/redux/serverRequests/GetUserNotificationsRequest';
import { IWorkbookInfo } from '../../workbook/models/IWorkbookInfo';
import { WorkbookAccelerationMapper } from '../../workbook/models/mappers/WorkbookAccelerationMapper';
import { WorkbookMapper } from '../../workbook/models/mappers/WorkbookMapper';
import { IGetWorkbookAccelerationsResult } from '../../workbook/ServerApi/requests/GetWorkbookAccelerationsRequest';
import { IGetWorkbooksResult } from '../../workbook/ServerApi/requests/GetWorkbooksRequest';
import { IWorkbookAccelerationInfo } from '../../workbook/ServerApi/types/IWorkbookAccelerationInfo';
import { FeatureFlags } from '../FeatureFlags';
import { AccelerationTaskMapper } from '../mappers/AccelerationTaskMapper';
import { AlertListMapper } from '../mappers/AlertMapper';
import { ContentLineageRelationshipMapper } from '../mappers/ContentLineageRelationshipMapper';
import { ContentMapper } from '../mappers/ContentMapper';
import { DataAlertMapper } from '../mappers/DataAlertMapper';
import { DatabaseWithCrassMapper } from '../mappers/DatabaseWithCrassMapper';
import { DataConnectionMapper } from '../mappers/DataConnectionMapper';
import { DataRoleMapper } from '../mappers/DataRoleMapper';
import { ExtractTaskMapper } from '../mappers/ExtractTaskMapper';
import { FlowMapper } from '../mappers/FlowMapper';
import { FlowOutputStepMapper } from '../mappers/FlowOutputStepMapper';
import { FlowOutputStepRunMapper } from '../mappers/FlowOutputStepRunMapper';
import { LensMapper } from '../mappers/LensMapper';
import { LinkedTaskMapper } from '../mappers/LinkedTaskMapper';
import { LocationMapper } from '../mappers/LocationMapper';
import { MetricMapper } from '../mappers/MetricMapper';
import { ProjectMapper } from '../mappers/ProjectMapper';
import { PublishedConnectionMapper } from '../mappers/PublishedConnectionMapper';
import { PublishedConnectionTableMapper } from '../mappers/PublishedConnectionTableMapper';
import { RemoteRefreshAgentMapper } from '../mappers/RemoteRefreshAgentMapper';
import { RemoteRefreshScheduleMapper } from '../mappers/RemoteRefreshScheduleMapper';
import { RunFlowTaskMapper } from '../mappers/RunFlowTaskMapper';
import { ShareActivityMapper } from '../mappers/ShareActivityMapper';
import { SharedWithMeMapper } from '../mappers/SharedWithMeMapper';
import { SubscriptionMapper } from '../mappers/SubscriptionMapper';
import { TableWithCrassMapper } from '../mappers/TableWithCrassMapper';
import { UserMapper } from '../mappers/UserMapper';
import { UserWithImageMapper } from '../mappers/UserWithImageMapper';
import { ViewMapper } from '../mappers/ViewMapper';
import { ContentLineageRelationshipInfo } from '../models/ContentLineageRelationshipInfo';
import { ExplorableItemType } from '../models/ContentType';
import { FlowListItemInfo } from '../models/FlowInfo';
import { FlowOutputStepInfo } from '../models/FlowOutputStepInfo';
import { FlowOutputStepRunListItemInfo } from '../models/FlowOutputStepRunInfo';
import { IAccelerationTaskInfo } from '../models/IAccelerationTaskInfo';
import { IAlertListInfo } from '../models/IAlertInfo';
import { IContentInfo } from '../models/IContentInfo';
import { IDataAlertInfo } from '../models/IDataAlertInfo';
import { IDatabaseInfo } from '../models/IDatabaseInfo';
import { IDataConnectionInfo } from '../models/IDataConnectionInfo';
import { IDataRoleInfo } from '../models/IDataRoleInfo';
import { IExtractTaskInfo } from '../models/IExtractTaskInfo';
import { IGroupInfo } from '../models/IGroupInfo';
import { ILensInfo } from '../models/ILensInfo';
import { IProjectInfo } from '../models/IProjectInfo';
import { IPublishedConnectionInfo } from '../models/IPublishedConnectionInfo';
import { IPublishedConnectionColumnInfo, IPublishedConnectionTableInfo } from '../models/IPublishedConnectionTableInfo';
import { IRecentContentInfo } from '../models/IRecentContentInfo';
import { IRemoteRefreshAgentInfo } from '../models/IRemoteRefreshAgentInfo';
import { IRemoteRefreshScheduleInfo } from '../models/IRemoteRefreshScheduleInfo';
import { IServerUserInfo } from '../models/IServerUserInfo';
import { ISharedVersionInfo } from '../models/ISharedVersionInfo';
import { ISharedWithMeContentInfo } from '../models/ISharedWithMeContentInfo';
import { ISiteUserInfo } from '../models/ISiteUserInfo';
import { ISubscriptionInfo } from '../models/ISubscriptionInfo';
import { ITableInfo } from '../models/ITableInfo';
import { IUserInfo } from '../models/IUserInfo';
import { IViewInfo } from '../models/IViewInfo';
import { IViewRecommendationInfo } from '../models/IViewRecommendationInfo';
import { LinkedTaskInfo } from '../models/LinkedTaskInfo';
import { RunFlowTaskInfo } from '../models/RunFlowTaskInfo';
import { mapContentFromCrass } from '../resources/Search';
import { CustomViewMapper } from '../ServerApi/CustomViews/CustomViewMapper';
import { IGetCustomizedViewsResult } from '../ServerApi/CustomViews/GetCustomizedViewsRequest';
import { ICustomViewInfo } from '../ServerApi/CustomViews/ICustomViewInfo';
import { IExploreContentResult } from '../ServerApi/requests/ExploreContentRequest';
import { ExploreFavoriteContentForUserResult } from '../ServerApi/requests/ExploreFavoriteContentForUserRequest';
import { IGetAccelerationTasksResult } from '../ServerApi/requests/GetAccelerationTasksRequest';
import { GetContentLineageRelationshipsResult } from '../ServerApi/requests/GetContentLineageRelationshipsRequest';
import { IGetDataAlertsResult } from '../ServerApi/requests/GetDataAlertsRequest';
import { IGetDatabasesResult } from '../ServerApi/requests/GetDatabasesRequest';
import { IGetDataConnectionsResult } from '../ServerApi/requests/GetDataConnectionsRequest';
import { IGetDataRolesResult } from '../ServerApi/requests/GetDataRolesRequest';
import { IGetDetailedRecentsResult } from '../ServerApi/requests/GetDetailedRecentsRequest';
import { IGetExtractTasksResult } from '../ServerApi/requests/GetExtractTasksRequest';
import { IGetFieldValuesResult } from '../ServerApi/requests/GetFieldValuesRequest';
import { GetFlowOutputStepRunsResult } from '../ServerApi/requests/GetFlowOutputStepRunsRequest';
import { GetFlowOutputStepsResult } from '../ServerApi/requests/GetFlowOutputStepsRequest';
import { GetFlowsResult } from '../ServerApi/requests/GetFlowsRequest';
import {
  FlowWithRunPermission,
  GetFlowsWithRunPermissionsResult
} from '../ServerApi/requests/GetFlowsWithRunPermissionsRequest';
import { IGetGroupsResult } from '../ServerApi/requests/GetGroupsRequest';
import { IGetLensesResult } from '../ServerApi/requests/GetLensesRequest';
import { GetLinkedTaskResult } from '../ServerApi/requests/GetLinkedTaskRequest';
import { IGetMetricsResult } from '../ServerApi/requests/GetMetricsRequest';
import { IGetProjectsResult } from '../ServerApi/requests/GetProjectsRequest';
import { IGetPublishedConnectionColumnsResult } from '../ServerApi/requests/GetPublishedConnectionColumnsRequest';
import { IGetPublishedConnectionsResult } from '../ServerApi/requests/GetPublishedConnectionsRequest';
import { IGetPublishedConnectionTablesResult } from '../ServerApi/requests/GetPublishedConnectionTablesRequest';
import { IGetRecommendedViewsResult } from '../ServerApi/requests/GetRecommendedViewsRequest';
import { IGetRemoteRefreshAgentsResult } from '../ServerApi/requests/GetRemoteRefreshAgentsRequest';
import { IRemoteRefreshSchedulesResult } from '../ServerApi/requests/GetRemoteRefreshSchedulesRequest';
import { GetRunFlowTasksResult } from '../ServerApi/requests/GetRunFlowTasksRequest';
import { IGetServerUsersResult } from '../ServerApi/requests/GetServerUsersRequest';
import { IGetShareActivityResult, ISharedVersion } from '../ServerApi/requests/GetShareActivityRequest';
import { IGetSharedWithMeResult } from '../ServerApi/requests/GetSharedWithMeRequest';
import { IGetSiteUsersResult } from '../ServerApi/requests/GetSiteUsersRequest';
import { IGetSubscriptionsResult } from '../ServerApi/requests/GetSubscriptionsRequest';
import { IGetTablesResult } from '../ServerApi/requests/GetTablesRequest';
import { IGetViewsResult } from '../ServerApi/requests/GetViewsRequest';
import { IAlertList } from '../ServerApi/types/IAlert';
import { IFieldValue } from '../ServerApi/types/IFieldValue';
import { IGroup } from '../ServerApi/types/IGroup';
import { IRecent } from '../ServerApi/types/IRecent';
import { IServerUser } from '../ServerApi/types/IServerUser';
import { ISiteUser } from '../ServerApi/types/ISiteUser';
import { IMetricUsageInfo, IUsageInfo } from '../ServerApi/types/IUsageInfo';
import { IViewRecommendation } from '../ServerApi/types/IViewRecommendation';
import { IIdItem, IPathItem } from '../ServerApi/types/resourceParts';
import { RecommendationsHelper } from './RecommendationsHelper';

export module ServerApiMapper {

  export function content(exploreContentResult: IExploreContentResult): IContentInfo[] {
    if(!exploreContentResult.content || exploreContentResult.content.length <= 0) return [];

    const users =  index(UserMapper.map(exploreContentResult.users));

    return ContentMapper.map(exploreContentResult.content, users);
  }

  export function favoriteFromCrass(result: ExploreFavoriteContentForUserResult): IContentInfo[] {
    if(!result.content || result.content.length <= 0) return [];

    return result.content.map((item: any) => {
      return {...mapContentFromCrass(item), favorite: true};
    });
  }

  export function projects(projectResult: IGetProjectsResult): IProjectInfo[] {
    if(!projectResult.projects || projectResult.projects.length <= 0) return [];

    const users = index(UserMapper.map(projectResult.users));

    return ProjectMapper.map(projectResult.projects, users, favorites(projectResult.favorites));
  }

  export function dataRoles(dataRoleResult: IGetDataRolesResult): IDataRoleInfo[] {
    if(!dataRoleResult.dataRoles || dataRoleResult.dataRoles.length <= 0) return [];

    const users = index(UserMapper.map(dataRoleResult.users));
    const projects = index(ProjectMapper.mapNames(dataRoleResult.projects));
    const locations = LocationMapper.map(dataRoleResult.containers);

    return DataRoleMapper.map(dataRoleResult.dataRoles, users, favorites(dataRoleResult.favorites), projects, locations);
  }

  export function lenses(lensResult: IGetLensesResult): ILensInfo[] {
    if(!lensResult.lenses || lensResult.lenses.length <= 0) {
      return [];
    }

    const users = index(UserMapper.map(lensResult.users));
    const projects = index(ProjectMapper.mapNames(lensResult.projects));

    return LensMapper.map(lensResult.lenses, users, favorites(lensResult.favorites), projects);
  }

  export function publishedConnections(result: IGetPublishedConnectionsResult): IPublishedConnectionInfo[] {
    if(!result.publishedConnections || result.publishedConnections.length <= 0) return [];

    const users = index(UserMapper.map(result.users));
    const projects = index(ProjectMapper.mapNames(result.projects));

    return PublishedConnectionMapper.map(result.publishedConnections, users, projects, favorites(result.favorites));
  }

  export function publishedConnectionTables(result: IGetPublishedConnectionTablesResult): IPublishedConnectionTableInfo[] {
    if (!result.publishedConnectionTables || result.publishedConnectionTables.length <= 0) return [];

    const users = index(UserMapper.map(result.users));
    const projects = index(ProjectMapper.mapNames(result.projects));

    return PublishedConnectionTableMapper.map(result.publishedConnectionTables, users, projects, favorites(result.favorites));
  }

  export function publishedConnectionColumns(result: IGetPublishedConnectionColumnsResult): IPublishedConnectionColumnInfo[] {
    if (!result.publishedConnectionColumns || result.publishedConnectionColumns.length <= 0) return [];

    return PublishedConnectionTableMapper.mapColumns(result.publishedConnectionColumns);
  }

  export function dataConnections(dataConnectionsResult: IGetDataConnectionsResult): IDataConnectionInfo[] {
    if (!dataConnectionsResult.dataConnections || dataConnectionsResult.dataConnections.length <= 0) {
      return [];
    }

    const users = index(UserMapper.map(dataConnectionsResult.users));
    const projects = index(ProjectMapper.mapNames(dataConnectionsResult.projects));
    const workbooks = index(dataConnectionsResult.workbooks);
    const dataSources = dataConnectionsResult.datasources
      ? index(DatasourceMapper.mapBasic(dataConnectionsResult.datasources, users, projects, workbooks))
      : {};
    const flows = dataConnectionsResult.flows
      ? index(FlowMapper.mapBasic(dataConnectionsResult.flows, users, projects))
      : {};

    return DataConnectionMapper.map(dataConnectionsResult.dataConnections, dataSources, flows);
  }

  export function dataSources(dataSourcesResult: IGetDatasourcesResult): IDataSourceInfo[] {
    if(!dataSourcesResult.datasources || dataSourcesResult.datasources.length <= 0) return [];

    const users = index(UserMapper.map(dataSourcesResult.users));
    const projects = index(ProjectMapper.mapNames(dataSourcesResult.projects));
    const workbooks = index(dataSourcesResult.workbooks);
    const locations = LocationMapper.map(dataSourcesResult.containers);

    return DatasourceMapper.map(dataSourcesResult.datasources, users, projects, locations, workbooks, dataSourcesResult.maxStats,
      favorites(dataSourcesResult.favorites));
  }

  export function databasesUsingCrass(databasesResult: IGetDatabasesResult): IDatabaseInfo[] {
    if(!databasesResult.databases || databasesResult.databases.length <= 0) return [];

    return DatabaseWithCrassMapper.map(databasesResult.databases, databasesResult.maxDownstreamDatasourcesViewModel, databasesResult.maxDownstreamWorkbooksViewModel);
  }

  export function tablesUsingCrass(tablesResult: IGetTablesResult): ITableInfo[] {
    if(!tablesResult.tables || tablesResult.tables.length <= 0) return [];

    return TableWithCrassMapper.map(tablesResult.tables, tablesResult.maxDownstreamDatasourcesViewModel, tablesResult.maxDownstreamWorkbooksViewModel);
  }

  export function workbooks(workbooksResult: IGetWorkbooksResult): IWorkbookInfo[] {
    if(!workbooksResult.workbooks || workbooksResult.workbooks.length <= 0) return [];

    const users = index(UserMapper.map(workbooksResult.users));
    const projects = index(ProjectMapper.mapNames(workbooksResult.projects));
    const locations = LocationMapper.map(workbooksResult.containers);

    return WorkbookMapper.map(workbooksResult.workbooks, users, projects, locations, workbooksResult.maxStats,
      favorites(workbooksResult.favorites));
  }

  export function views(viewResult: IGetViewsResult): IViewInfo[] {
    if(!viewResult.views || viewResult.views.length <= 0) return [];

    const users = index(UserMapper.map(viewResult.users));
    const projects = index(ProjectMapper.mapNames(viewResult.projects));
    const locations = LocationMapper.map(viewResult.containers);
    const workbooks = index(WorkbookMapper.mapParents(viewResult.workbooks, users, projects, locations));
    const recents = _.indexBy(viewResult.recents, 'objectId');
    const viewAccelerations = _.indexBy(viewResult.viewAccelerations, 'viewId');

    return ViewMapper.map(viewResult.views, workbooks, viewResult.maxStats, favorites(viewResult.favorites), recents, viewAccelerations);
  }

  export function userNotifications(results: IGetUserNotificationsResult): IUserNotification<any>[] {
    if(!results.userNotifications || results.userNotifications.length <= 0) return [];

    return _.map(results.userNotifications, (notification): IUserNotification<any> => {
      return {
        ...notification,
        eventTime: new Date(notification.eventTime),
        creationTime: new Date(notification.creationTime),
        targetInfo: {...notification.targetInfo, type: TableauEntityType[notification.targetInfo.type]}
      };
    });
  }

  export function recentItems(recentsResult: IGetDetailedRecentsResult): IRecentContentInfo[] {
    if (!recentsResult.recents || recentsResult.recents.length <= 0){
      return [];
    }

    const users = index(UserMapper.map(recentsResult.users));
    const projects = index(ProjectMapper.mapNames(recentsResult.projects));
    const locations = LocationMapper.map(recentsResult.containers);
    const workbooks = index(WorkbookMapper.mapParents(recentsResult.workbooks, users, projects, locations));
    const views = index(ViewMapper.map(recentsResult.views, workbooks, recentsResult.viewMaxStats, favorites(recentsResult.viewFavorites)));
    const connectedViews = recentsResult.connectedViews ? indexByPath(MetricMapper.mapConnectedViews(recentsResult.connectedViews)) : {};
    const metrics = recentsResult.metrics ? index(MetricMapper.map(recentsResult.metrics, users, projects, [], connectedViews, <IMetricUsageInfo>{}, favorites(recentsResult.metricFavorites)))
      : {};
    const flows = recentsResult.flows ? index(FlowMapper.map(recentsResult.flows, users, projects, [], <IUsageInfo>{}, favorites(recentsResult.flowFavorites)))
    : {};

    const itemsInRecentOrder: IRecentContentInfo[] = recentsResult.recents
    .filter((recent: IRecent) => recent.objectType === 'view'
    || recent.objectType === 'metric'
    || recent.objectType === 'flow')
      .map((recent: IRecent) => {
        switch(recent.objectType){
          case 'view':
            return {...views[recent.objectId], lastAccessedAt: recent.lastAccessedAt};
          case 'metric':
            return {...metrics[recent.objectId], lastAccessedAt: recent.lastAccessedAt};
          case 'flow':
            return {...flows[recent.objectId], lastAccessedAt: recent.lastAccessedAt};
        }
      });

    return itemsInRecentOrder;
  }

  export function recentUniqueUsers(recentsResult: IGetDetailedRecentsResult): IUserInfo[] {
    if (!recentsResult.recents || recentsResult.recents.length <= 0){
      return [];
    }

    const users = index(UserMapper.map(recentsResult.users));
    const projects = index(ProjectMapper.mapNames(recentsResult.projects));
    const locations = LocationMapper.map(recentsResult.containers);
    const workbooks = index(WorkbookMapper.mapParents(recentsResult.workbooks, users, projects, locations));
    const views = index(ViewMapper.map(recentsResult.views, workbooks, recentsResult.viewMaxStats, favorites(recentsResult.viewFavorites)));
    const connectedViews = recentsResult.connectedViews ? indexByPath(MetricMapper.mapConnectedViews(recentsResult.connectedViews)) : {};
    const metrics = recentsResult.metrics ? index(MetricMapper.map(recentsResult.metrics, users, projects, [], connectedViews, <IMetricUsageInfo>{}, favorites(recentsResult.metricFavorites)))
      : {};
    const flows = recentsResult.flows ? index(FlowMapper.map(recentsResult.flows, users, projects, [], <IUsageInfo>{}, favorites(recentsResult.flowFavorites)))
      : {};

    const recentUsers: IUserInfo[] = recentsResult.recents
      .filter((recent: IRecent) => recent.objectType === 'view'
        || recent.objectType === 'metric'
        || recent.objectType === 'flow')
      .map((recent: IRecent) => {
        switch(recent.objectType){
          case 'view':
            return views[recent.objectId].workbook.owner;
          case 'metric':
            return metrics[recent.objectId].owner;
          case 'flow':
            return flows[recent.objectId].owner;
        }
      });

    if (FeatureFlags.aggregateRecentUsers) {
      let idToCount = new Map();
      let idToUser = new Map();

      recentUsers.forEach(user => {
        if (idToCount.has(user.id)) {
          idToCount.set(user.id, idToCount.get(user.id) + 1);
        } else {
          idToCount.set(user.id, 1);
          idToUser.set(user.id, user);
        }
      });

      let userCounts = Array.from(idToCount.entries());
      userCounts.sort((a, b) => { return b[1] - a[1]; });

      return userCounts.map(u => idToUser.get(u[0]));
    } else {
      let userIds = new Set();
      let uniqueUsers: IUserInfo[] = [];

      recentUsers.forEach(user => {
        if (!userIds.has(user.id)) {
          userIds.add(user.id);
          uniqueUsers.push(user);
        }
      });

      return uniqueUsers;
    }
  }

  export function metrics(metricsResult: IGetMetricsResult): IMetricInfo[] {
    if(!metricsResult.metrics || metricsResult.metrics.length <= 0) return [];

    const users = index(UserMapper.map(metricsResult.users));
    const connectedViews = indexByPath(MetricMapper.mapConnectedViews(metricsResult.connectedViews));
    const projects = index(ProjectMapper.mapNames(metricsResult.projects));
    const locations = LocationMapper.map(metricsResult.containers);

    return MetricMapper.map(metricsResult.metrics, users, projects, locations, connectedViews, metricsResult.maxStats, favorites(metricsResult.favorites));
  }

  export function vizRecommendations(vizRecommendationsResult: IGetRecommendedViewsResult): IViewRecommendationInfo[] {
    if(!vizRecommendationsResult.recommendations || vizRecommendationsResult.recommendations.length <= 0) return [];

    const users = index(UserMapper.map(vizRecommendationsResult.users));
    const projects = index(ProjectMapper.mapNames(vizRecommendationsResult.projects));
    const workbooks = index(WorkbookMapper.mapParents(vizRecommendationsResult.workbooks, users, projects));
    const views = index(ViewMapper.map(vizRecommendationsResult.views, workbooks, vizRecommendationsResult.viewMaxStats));
    const modelId = vizRecommendationsResult.modelId;

    const rankedRecs: IViewRecommendationInfo[] = RecommendationsHelper.rankRecommendations(vizRecommendationsResult.recommendations)
      // filter out views from recommendation model that is no longer available in views array
      .filter((vizRec: IViewRecommendation) => views[vizRec.objectId])
      .map((vizRec: IViewRecommendation) => {
        const recommendedView = {...views[vizRec.objectId], contentType: <'recommendedView'> 'recommendedView'};
        return {
            ...recommendedView,
            modelId: modelId,
            hide: false,
            favoritesTotal: recommendedView.usageInfo.favoritesTotal,
            explanationType: vizRec.explanation.type,
            similiarUsers: vizRec.explanation.userIds && vizRec.explanation.userIds.map(userId => users[userId])
          };
      }) || [];
    return rankedRecs;
  }

  export function shareActivity(shareActivityResult: IGetShareActivityResult): ISharedVersionInfo[] {
    if (!shareActivityResult.shareActions || shareActivityResult.shareActions.length <= 0) return [];

    const deletedSharers = index(UserMapper.map(shareActivityResult.deletedSharers));
    const sharers = index(UserWithImageMapper.map(shareActivityResult.sharers));

    let sharedVersionsByTypeThenId: _.Dictionary<_.Dictionary<ISharedVersion>> = {};
    const sharedVersionsByType = _.groupBy(shareActivityResult.viewOrCustomizedViews, 'contentType');
    Object.keys(sharedVersionsByType).forEach((type: string) => {
      sharedVersionsByTypeThenId[type] = index(sharedVersionsByType[type]);
    });

    return ShareActivityMapper.map(shareActivityResult.shareActions, sharedVersionsByTypeThenId, sharers, deletedSharers);
  }

  export function sharedWithMe(sharedWithMeResult: IGetSharedWithMeResult): ISharedWithMeContentInfo[] {
    if (!sharedWithMeResult.shareActions || sharedWithMeResult.shareActions.length <= 0) return [];

    const deletedSharers = index(UserMapper.map(sharedWithMeResult.deletedSharers));
    const owners = index(UserMapper.map(sharedWithMeResult.owners));
    const sharers = index(UserWithImageMapper.map(sharedWithMeResult.sharers));
    const customViews = index(sharedWithMeResult.underhoodCustomizedViews);
    const contentList = ContentMapper.map(sharedWithMeResult.content, owners);

    const contentByType = groupByContentType(contentList);
    let contentByTypeThenId: _.Dictionary<_.Dictionary<IContentInfo<ExplorableItemType>>> = {};
    Object.keys(contentByType).forEach((type: string) => {
      let contents = index(contentByType[type]);
      contentByTypeThenId[type] = contents;
    });

    return SharedWithMeMapper.map(sharedWithMeResult.shareActions, contentByTypeThenId, sharers, deletedSharers, sharedWithMeResult.viewsWithShareActivity, customViews);
  }

  export function flows(flowsResult: GetFlowsResult): FlowListItemInfo[] {
    if(!flowsResult.flows || flowsResult.flows.length <= 0) return [];

    const users = index(UserMapper.map(flowsResult.users));
    const projects = index(ProjectMapper.mapNames(flowsResult.projects));
    const locations = LocationMapper.map(flowsResult.containers);

    return FlowMapper.map(flowsResult.flows, users, projects, locations, flowsResult.maxStats, favorites(flowsResult.favorites));
  }

  export function flowsWithPermissions(flowsResult: GetFlowsWithRunPermissionsResult): FlowWithRunPermission[] {
    if(!flowsResult.contents || flowsResult.contents.length <= 0) return [];
    return flowsResult.contents;
  }


  export function flowOutputSteps(flowOutputStepsResult: GetFlowOutputStepsResult): FlowOutputStepInfo[] {
    if(!flowOutputStepsResult.outputSteps || flowOutputStepsResult.outputSteps.length <= 0) return [];

    const connections = index(flowOutputStepsResult.dataConnections);
    const datasources = index(flowOutputStepsResult.datasources);

    return FlowOutputStepMapper.map(flowOutputStepsResult.outputSteps, connections, datasources, null);
  }

  export function flowOutputStepRuns(flowOutputStepRunsResult: GetFlowOutputStepRunsResult): FlowOutputStepRunListItemInfo[] {
    if (!flowOutputStepRunsResult.flowOutputStepRuns || flowOutputStepRunsResult.flowOutputStepRuns.length <= 0) return [];

    const outputSteps = index(flowOutputStepRunsResult.flowOutputSteps);
    const runs = index(flowOutputStepRunsResult.flowRuns);

    return FlowOutputStepRunMapper.map(flowOutputStepRunsResult.flowOutputStepRuns, outputSteps, runs);
  }

  export function contentLineageRelationships(contentLineageRelationshipsResult: GetContentLineageRelationshipsResult): ContentLineageRelationshipInfo[] {
    if (contentLineageRelationshipsResult?.contentLineageRelationships?.length <= 0) return [];

    const contentLineageRelationships = contentLineageRelationshipsResult.contentLineageRelationships;

    return ContentLineageRelationshipMapper.map(contentLineageRelationships);
  }

  export function alertList(alertList: IAlertList): IAlertListInfo {
    if(!(alertList && alertList.alerts && alertList.alerts.length > 0)) return { total: 0, alerts: [] };

    const workbooks = index(alertList.workbooks);
    const datasources = index(alertList.datasources);
    const flowOutputStepRuns = index(alertList.flowOutputStepRuns);
    const flowOutputSteps = index(alertList.flowOutputSteps);
    const flows = index(alertList.flows);

    return AlertListMapper.map(alertList.totalCount, alertList.alerts, workbooks, datasources, flowOutputStepRuns, flowOutputSteps, flows);
  }

  export function accelerationTasks(accelerationTaskResult: IGetAccelerationTasksResult): IAccelerationTaskInfo[] {
    if(_.size(accelerationTaskResult.tasks) <= 0) return [];

    const views =   index(accelerationTaskResult.views);
    const workbooks =   index(accelerationTaskResult.workbooks);
    const projects =    index(accelerationTaskResult.projects);
    const users =    index(accelerationTaskResult.users);
    const sites =    index(accelerationTaskResult.sites);

    return AccelerationTaskMapper.map(accelerationTaskResult.tasks, views, workbooks, projects, users, sites);
  }

  export function extractTasks(extractTaskResult: IGetExtractTasksResult): IExtractTaskInfo[] {
    if(_.size(extractTaskResult.tasks) <= 0) return [];

    const schedules =   index(extractTaskResult.schedules);
    const sites =       index(extractTaskResult.sites);
    const workbooks =   index(extractTaskResult.workbooks);
    const datasources = index(extractTaskResult.datasources);
    const publishedConnections = index(extractTaskResult.publishedConnections);
    const extractServiceTasks = index(extractTaskResult.extractServiceTasks);

    return ExtractTaskMapper.map(extractTaskResult.tasks, schedules, sites, workbooks, datasources, publishedConnections, extractServiceTasks);
  }

  export function workbookAccelerations(workbookAccelerationResult: IGetWorkbookAccelerationsResult): IWorkbookAccelerationInfo[] {
    if(!workbookAccelerationResult.views || workbookAccelerationResult.views.length <= 0) return [];

    const viewAccelerationPerfs = _.indexBy(workbookAccelerationResult.viewAccelerationPerfs, 'viewId');
    const viewAccelerationStatus = _.indexBy(workbookAccelerationResult.viewAccelerationStatus, 'viewId');

    return WorkbookAccelerationMapper.map(workbookAccelerationResult.views, workbookAccelerationResult.maxStats, viewAccelerationPerfs, viewAccelerationStatus);
  }

  export function runFlowTasks(runFlowTasksResult: GetRunFlowTasksResult): RunFlowTaskInfo[] {
    if (runFlowTasksResult == null) return;
    if (_.size(runFlowTasksResult.tasks) <= 0) return [];

    const schedules =           index(runFlowTasksResult.schedules);
    const sites =               index(runFlowTasksResult.sites);
    const flows =               index(runFlowTasksResult.flows);
    const flowOutputSteps =     index(runFlowTasksResult.flowOutputSteps);
    const flowOutputStepRuns =  runFlowTasksResult.flowOutputStepRuns;
    const flowRuns =            runFlowTasksResult.flowRuns;
    const dataConnections =     index(runFlowTasksResult.dataConnections);

    return RunFlowTaskMapper.map(runFlowTasksResult.tasks, schedules, sites, flows, dataConnections, flowOutputSteps, flowRuns, flowOutputStepRuns);
  }

  export function linkedTask(getLinkedTaskResult: GetLinkedTaskResult): LinkedTaskInfo {
    if (getLinkedTaskResult == null) return;
    const flows = index(getLinkedTaskResult.flows);
    const outputSteps = index(getLinkedTaskResult.outputSteps);
    const dataConnections = index(getLinkedTaskResult.dataConnections);
    const datasources = index(getLinkedTaskResult.datasources);

    return LinkedTaskMapper.map(getLinkedTaskResult.linkedTask, flows, outputSteps, dataConnections, datasources);
  }

  export function remoteRefreshSchedules(remoteRefreshSchedulesResult: IRemoteRefreshSchedulesResult): IRemoteRefreshScheduleInfo[] {
    if(_.size(remoteRefreshSchedulesResult.schedules) <= 0) return [];

    return RemoteRefreshScheduleMapper.mapToResource(remoteRefreshSchedulesResult);
  }

  export function remoteRefreshAgents(remoteRefreshAgentsResult: IGetRemoteRefreshAgentsResult): IRemoteRefreshAgentInfo[] {
    if(_.size(remoteRefreshAgentsResult.agents) <= 0) return [];

    return RemoteRefreshAgentMapper.mapToResource(remoteRefreshAgentsResult.agents);
  }

  export function customViews(customViewResult: IGetCustomizedViewsResult): ICustomViewInfo[] {
    if(_.size(customViewResult.customViews) <= 0) return [];

    return CustomViewMapper.map(
      customViewResult.customViews,
      index(customViewResult.sites),
      customViewResult.maxStats,
      index(customViewResult.users),
    );
  }

  export function subscriptions(subscriptionResult: IGetSubscriptionsResult): ISubscriptionInfo[] {
    if(_.size(subscriptionResult.subscriptions) <= 0) return [];

    return SubscriptionMapper.map(
      subscriptionResult.subscriptions,
      index(subscriptionResult.schedules),
      index(subscriptionResult.sites),
      index(subscriptionResult.users)
    );
  }

  export function dataAlerts(dataAlertsResult: IGetDataAlertsResult): IDataAlertInfo[] {
    if(_.size(dataAlertsResult.dataAlerts) <= 0) return [];

    return DataAlertMapper.map(
      dataAlertsResult.dataAlerts,
      index(dataAlertsResult.users),
      index(dataAlertsResult.groups)
    );
  }

  export function groups(groupsResult: IGetGroupsResult): IGroupInfo[] {
    return groupsResult.groups.map((group: IGroup) => {
      const groupInfo: IGroupInfo = {
        ...group,
        contentType: 'group'
      };
      return groupInfo;
    });
  }

  export function siteUsers(siteUsersResult: IGetSiteUsersResult): ISiteUserInfo[] {
    return siteUsersResult.users.map((user: ISiteUser) => {
      const userInfo: ISiteUserInfo = {
        ...user,
        contentType: 'siteuser'
      };
      return userInfo;
    });
  }

  export function serverUsers(serverUsersResult: IGetServerUsersResult): IServerUserInfo[] {
    return serverUsersResult.users.map((user: IServerUser) => {
      const userInfo: IServerUserInfo = {
        ...user,
        contentType: 'serveruser'
      };
      return userInfo;
    });
  }

  export function userIdentityConflicts(userIdentityConflictsResult: IGetUserIdentityConflictsResult): IUserIdentityConflictInfo[] {
    return userIdentityConflictsResult.userIdentityConflicts.map((user: IUserIdentityConflict) => {
      const identityConflictInfo: IUserIdentityConflictInfo = {
        ...user,
        contentType: 'userIdentityConflict'
      };
      return identityConflictInfo;
    });
  }

  export function fieldValues(fieldValueResult: IGetFieldValuesResult): IFieldValue[] {
    return fieldValueResult.fieldValues;
  }

  function favorites(ids: string[]) {
    return _.indexBy(ids, _.identity);
  }

  function index<T extends IIdItem>(items: ReadonlyArray<T>): _.Dictionary<T> {
    return _.indexBy(items, 'id');
  }

  function indexByPath<T extends IPathItem>(items: ReadonlyArray<T>): _.Dictionary<T> {
    return _.indexBy(items, 'path');
  }

  function groupByContentType<T extends IContentInfo>(items: ReadonlyArray<T>): _.Dictionary<T[]> {
    return _.groupBy(items, 'contentType');
  }
}
